import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { formatDateTime } from '../../services/expenseVouchersApi';
import { operationsApi, REMINDER_STAGE_LABELS } from '../../services/expenseOperationsApi';
import { ReportsHeader } from '../../components/expense/ReportShared';

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const headerCell = { color: 'white', fontWeight: 600, whiteSpace: 'nowrap' };

// Weekly pending reminders (Accounts): the schedule and switches, and a preview of who would
// be reminded about what this week. The preview sends nothing.
const AccountsReminders = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData((await operationsApi.reminders()).data);
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    if (!window.confirm('Send this week\'s pending reminders now? Anyone already reminded this week is skipped.')) return;
    setRunning(true);
    try {
      const r = (await operationsApi.runReminders()).data;
      toast.success(`Reminders sent to ${r.sent} recipient(s) for ${r.vouchers} voucher(s); ${r.skippedDuplicates} already reminded this week.`);
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setRunning(false);
    }
  };

  const s = data?.status;
  const preview = data?.preview?.preview || [];

  return (
    <Box sx={{ bgcolor: 'background.default', p: 3 }}>
      <ReportsHeader
        title="Weekly Pending Reminders"
        subtitle="Each week, people with expense vouchers waiting for their action get one reminder per voucher. Draft, sent back, rejected, paid, cancelled and on-hold vouchers are never reminded."
      />
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error.message}</Alert>}
      {loading && !data && <CircularProgress />}

      {s && (
        <>
          <Alert severity={s.enabled ? 'success' : 'info'} sx={{ mb: 2 }} data-testid="reminder-status">
            {s.enabled
              ? `Enabled: runs every ${WEEKDAYS[s.weekday]} from ${String(s.hour).padStart(2, '0')}:00 (server time). Email ${s.emails ? 'on' : 'off'} - in-app notifications only when off.`
              : 'Disabled on this server: no reminders are sent (set EXPENSE_REMINDERS_ENABLED=true to enable). The preview below shows what would be sent.'}
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
              Week {s.weekKey}: {s.sentThisWeek} reminder(s) to {s.recipientsThisWeek} person(s) so far · last sent {formatDateTime(s.lastSentAt)} · items waiting at least {s.minAgeHours}h
            </Typography>
          </Alert>

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Refresh preview</Button>
            <Button variant="contained" onClick={runNow} disabled={!s.enabled || running}>Send reminders now</Button>
          </Box>

          <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
            <Table size="small" data-testid="reminder-preview">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'primary.main' }}>
                  {['Recipient', 'Voucher', 'Employee', 'Waiting for', 'Waiting since', 'This week'].map((h) => <TableCell key={h} sx={headerCell}>{h}</TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody>
                {preview.length === 0 && (
                  <TableRow><TableCell colSpan={6}><Typography color="text.secondary">Nothing is pending long enough to remind.</Typography></TableCell></TableRow>
                )}
                {preview.flatMap((p) => p.items.map((i, idx) => (
                  <TableRow key={`${p.recipient}-${i.voucherId}`}>
                    <TableCell>{idx === 0 ? <><strong>{p.recipientName}</strong><br /><Typography variant="caption">{p.recipientEmail}</Typography></> : ''}</TableCell>
                    <TableCell>{i.voucherNumber}</TableCell>
                    <TableCell>{i.employeeName}</TableCell>
                    <TableCell>{i.stages.map((st) => REMINDER_STAGE_LABELS[st] || st).join(', ')}</TableCell>
                    <TableCell>{formatDateTime(i.pendingSince)}</TableCell>
                    <TableCell>{i.alreadySentThisWeek ? <Chip size="small" label="Reminded" /> : <Chip size="small" color="warning" label="Due" />}</TableCell>
                  </TableRow>
                )))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};

export default AccountsReminders;
